import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

export const getRicefwItems = async (projectId) => {
  const response = await axios.get(`${API_BASE_URL}/api/ricefw/${projectId}`, {
    withCredentials: true,
  });
  return response.data;
};

export const generateRicefwItems = async (projectId) => {
  const response = await axios.post(
    `${API_BASE_URL}/api/ricefw/${projectId}/generate`,
    {},
    { withCredentials: true }
  );
  return response.data;
};

export const downloadRicefwExcel = async (projectId) => {
  const url = `${API_BASE_URL}/api/ricefw/${projectId}/export`;
  try {
    const response = await fetch(url, { credentials: 'include' });
    if (!response.ok) {
      const contentType = response.headers.get('content-type') || '';
      let error = 'Failed to download RICEFW list';
      if (contentType.includes('application/json')) {
        const data = await response.json();
        error = data.error || error;
      }
      return { ok: false, error };
    }
    const blob = await response.blob();
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `RICEFW_List_${projectId}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
    return { ok: true };
  } catch (error) {
    return { ok: false, error: error.message };
  }
};

export const uploadRicefwPreview = async (projectId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await fetch(`${API_BASE_URL}/api/ricefw/${projectId}/upload-preview`, {
    method: 'POST',
    credentials: 'include',
    body: formData,
  });
  return response.json();
};

export const uploadRicefwApply = async (projectId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await fetch(`${API_BASE_URL}/api/ricefw/${projectId}/upload-apply`, {
    method: 'POST',
    credentials: 'include',
    body: formData,
  });
  return response.json();
};

export const updateRicefwItem = async (itemId, data) => {
  const response = await axios.put(`${API_BASE_URL}/api/ricefw/item/${itemId}`, data, {
    withCredentials: true,
  });
  return response.data;
};

export const deleteRicefwItems = async (projectId, ids) => {
  const response = await axios.delete(`${API_BASE_URL}/api/ricefw/${projectId}/items`, {
    withCredentials: true,
    data: { ids },
  });
  return response.data;
};

const _downloadDocFromEndpoint = async (url, ids, defaultFilename) => {
  try {
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids }),
    });
    if (!response.ok) {
      const contentType = response.headers.get('content-type') || '';
      let error = 'Failed to generate document(s)';
      if (contentType.includes('application/json')) {
        const d = await response.json();
        error = d.error || error;
      }
      return { ok: false, error };
    }
    const disposition = response.headers.get('content-disposition') || '';
    const match = disposition.match(/filename="?([^";\n]+)"?/);
    const filename = match ? match[1] : defaultFilename;
    const blob = await response.blob();
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
    return { ok: true };
  } catch (error) {
    return { ok: false, error: error.message };
  }
};

export const generateFsDocuments = async (projectId, ids) => {
  const defaultName = ids.length === 1 ? `FS_${ids[0]}.docx` : `RICEFW_FS_${projectId}.zip`;
  return _downloadDocFromEndpoint(
    `${API_BASE_URL}/api/ricefw-fs/${projectId}/generate`,
    ids,
    defaultName
  );
};

export const generateTsDocuments = async (projectId, ids) => {
  const defaultName = ids.length === 1 ? `TS_${ids[0]}.docx` : `RICEFW_TS_${projectId}.zip`;
  return _downloadDocFromEndpoint(
    `${API_BASE_URL}/api/ricefw-fs/${projectId}/generate-ts`,
    ids,
    defaultName
  );
};

const _downloadBlobResponse = async (response, defaultFilename) => {
  if (!response.ok) {
    const contentType = response.headers.get('content-type') || '';
    let error = 'Failed to download document';
    if (contentType.includes('application/json')) {
      const data = await response.json();
      error = data.error || error;
    }
    return { ok: false, error };
  }
  const disposition = response.headers.get('content-disposition') || '';
  const match = disposition.match(/filename="?([^";\n]+)"?/);
  const filename = match ? match[1] : defaultFilename;
  const blob = await response.blob();
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
  return { ok: true, filename };
};

export const getRicefwSpecSummary = async (projectId) => {
  const response = await axios.get(`${API_BASE_URL}/api/ricefw-spec-docs/${projectId}/summary`, {
    withCredentials: true,
  });
  return response.data;
};

export const getRicefwSpecDocument = async (projectId, itemId, specType) => {
  const response = await axios.get(
    `${API_BASE_URL}/api/ricefw-spec-docs/${projectId}/items/${itemId}/${specType}`,
    { withCredentials: true }
  );
  return response.data;
};

export const downloadRicefwSpecDocument = async (projectId, itemId, specType, version = null) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/ricefw-spec-docs/${projectId}/items/${itemId}/${specType}/download`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(version === null || version === undefined ? {} : { version }),
      }
    );
    const prefix = specType === 'functional' ? 'FS' : 'TS';
    return _downloadBlobResponse(response, `${prefix}_${itemId}.docx`);
  } catch (error) {
    return { ok: false, error: error.message };
  }
};

const _appendSpecUploadFields = (formData, file, changeNote, baseVersion = null) => {
  formData.append('document', file);
  formData.append('change_note', changeNote);
  if (baseVersion !== null && baseVersion !== undefined) {
    formData.append('base_version', String(baseVersion));
  }
};

export const previewRicefwSpecDocumentUpload = async (projectId, itemId, specType, file, changeNote = '', baseVersion = null) => {
  const formData = new FormData();
  _appendSpecUploadFields(formData, file, changeNote, baseVersion);
  const response = await fetch(
    `${API_BASE_URL}/api/ricefw-spec-docs/${projectId}/items/${itemId}/${specType}/upload-preview`,
    {
      method: 'POST',
      credentials: 'include',
      body: formData,
    }
  );
  return response.json();
};

export const uploadRicefwSpecDocument = async (projectId, itemId, specType, file, changeNote = '', baseVersion = null) => {
  const formData = new FormData();
  _appendSpecUploadFields(formData, file, changeNote, baseVersion);
  const response = await fetch(
    `${API_BASE_URL}/api/ricefw-spec-docs/${projectId}/items/${itemId}/${specType}/upload`,
    {
      method: 'POST',
      credentials: 'include',
      body: formData,
    }
  );
  return response.json();
};

export const copyRicefwItems = async (projectId, ids) => {
  const response = await axios.post(
    `${API_BASE_URL}/api/ricefw/${projectId}/copy`,
    { ids },
    { withCredentials: true }
  );
  return response.data;
};

export const createRicefwItem = async (projectId, data) => {
  const response = await axios.post(
    `${API_BASE_URL}/api/ricefw/${projectId}/item`,
    data,
    { withCredentials: true }
  );
  return response.data;
};
